// 11 §7 — the bottom bar's ends. Left: the selected hero ("Teo · Ranger / 2 actions left") and
// their two action slots, plus a starred bonus slot only once one is planned (click opens a slot;
// flags show the reason). Right: UNDO (clears the open or last
// planned slot) and LOCK PLAN (= ready, Space: commits every hero of mine, empty slots holding; the
// pulse resolves early once every human has locked). The lock says how many have locked, and who
// is still planning once two or fewer are left.
import { HotkeyKbd } from '../components';
import { ariaKeys, useHotkeyOverrides } from '../../core/input/hotkeys';
import { OFFLINE_TITLE, useLinkUp } from '../../core/stores/link';
import { useAuthoring } from '../../core/stores/authoring';
import { useEncounter } from '../../core/encounter/session';
import { useNames, usePulse, useReadyRows } from '../../core/subscriptions/hooks';
import { getConn, identityHex } from '../../core/net/connection';
import { toggleReady } from '../useKeyboard';
import { usePlayback } from '../../core/stores/playback';
import { actionFor } from '../../core/sim/actions';
import { SLOTS, type Slot } from '../../core/sim/types';
import { describeIntent, useSeats } from './shared';
import { BONUS_MARK, slotHints, slotMark } from './slots';

const REASONS: Record<string, string> = {
  path_blocked: 'path blocked', target_dead: 'target already dead', destination_taken: 'destination taken',
  out_of_range: 'out of range after a move', illegal: 'illegal', moved_by_enemy: 'target moved by an enemy',
};

export function ActionQueue() {
  const heroId = useAuthoring((s) => s.selectedHero);
  const slot = useAuthoring((s) => s.slot);
  const local = useAuthoring((s) => s.local);
  const rejected = useAuthoring((s) => s.rejected);
  const rows = useAuthoring((s) => s.rows);
  const projected = useAuthoring((s) => s.projected);
  const chooseSlot = useAuthoring((s) => s.chooseSlot);
  const snapshot = useEncounter((s) => s.snapshot);
  const owners = useEncounter((s) => s.owners);
  const { colour } = useSeats();
  const hero = heroId === undefined ? undefined : snapshot?.units.find((u) => u.heroId === heroId);
  if (!hero) return <div className="fk-hud__who"><span className="fk-hud__who-name">No hero</span><span className="fk-hud__who-sub">Tab or click a hero</span></div>;
  const cls = hero.classId ? hero.classId.replace(/^hero_/, '') : 'hero';

  return (
    <div className="fk-hud__who" style={{ ['--fk-seat' as string]: colour(owners[hero.heroId!]) }}>
      <span className="fk-hud__who-name">{hero.name} · <span>{cls.charAt(0).toUpperCase() + cls.slice(1)}</span></span>
      <span className="fk-hud__who-sub">{left(local, rows, heroId!)}</span>
      <span className="fk-hud__slots" role="list" aria-label={`Slots for ${hero.name}`}>
        {slotHints(heroId!, local, rows, slot).map((s) => {
          const key = `${heroId}:${s}`;
          const l = local.get(key); const row = rows.get(key); const rej = rejected.get(key);
          const intent = l ?? row;
          const def = intent ? actionFor(intent.action, hero) : undefined;
          const flag = projected?.conflicts.find((c) => c.intent.heroId === heroId && c.intent.slot === s);
          const state = rej ? 'rejected' : l ? 'local' : row?.state ?? '';
          const label = intent ? describeIntent(intent.action, intent.args, snapshot?.units, hero) : 'empty';
          const name = s === 'B' ? `Bonus ${BONUS_MARK}` : `Slot ${s}`;
          const why = flag ? `, ${flag.severity} flag: ${REASONS[flag.reason] ?? flag.reason}` : rej ? `, rejected: ${rej.reason}` : '';
          return (
            <button key={String(s)} type="button" role="listitem" className={`fk-hud__slot fk-hit ${s === 'B' ? 'fk-hud__slot--bonus' : ''} ${state ? `fk-hud__slot--${state}` : ''} ${flag ? `fk-hud__slot--flag-${flag.severity}` : ''}`} aria-pressed={slot === s}
              data-testid={`queue-slot-${s}`} data-sfx="none" data-state={state} onClick={() => chooseSlot(s)} title={`${name}: ${label}${row?.substituted ? ' (substituted)' : ''}${why}`} aria-label={`${name}: ${label}${why}`}>
              <b>{slotMark(s)}</b><span>{def ? def.name : '—'}</span>
            </button>
          );
        })}
      </span>
    </div>
  );
}

/** "2 actions left", "1 action left", "all planned": what this hero still has this pulse (a bonus is extra, never counted as owed). */
function left(local: Map<string, unknown>, rows: Map<string, unknown>, heroId: number): string {
  const free = (s: (typeof SLOTS)[number]) => !local.has(`${heroId}:${s}`) && !rows.has(`${heroId}:${s}`);
  const n = Number(free(1)) + Number(free(2));
  return n === 0 ? 'all planned' : `${n} action${n > 1 ? 's' : ''} left`;
}

/** The slot Undo clears for `heroId`: the open slot if it holds something, else the last one filled. */
function undoSlotOf(heroId: number | undefined, slot: Slot | undefined, local: Map<string, unknown>, rows: Map<string, unknown>): Slot | undefined {
  const has = (s: Slot) => heroId !== undefined && (local.has(`${heroId}:${s}`) || rows.has(`${heroId}:${s}`));
  return slot !== undefined && has(slot) ? slot : [...SLOTS].reverse().find(has);
}

/** docs/83 PO12: Ctrl/Cmd+Z — what the Undo button does, when it could be pressed. */
export function undoLast(): void {
  const a = useAuthoring.getState();
  const heroId = a.selectedHero;
  if (heroId === undefined || lockedNow()) return;
  const s = undoSlotOf(heroId, a.slot, a.local, a.rows);
  if (s !== undefined) a.clear(heroId, s);
}

/** Is my plan locked this turn (from the rows, outside React)? */
function lockedNow(): boolean {
  const { encounterId, me } = useEncounter.getState();
  const c = getConn();
  if (!encounterId || !c) return false;
  return [...c.db.readyState.iter()].some((r) => r.encounterId === encounterId && identityHex(r.owner) === me && r.ready);
}

export function PlanButtons() {
  const keys = useHotkeyOverrides();
  const up = useLinkUp();
  const heroId = useAuthoring((s) => s.selectedHero);
  const slot = useAuthoring((s) => s.slot);
  const local = useAuthoring((s) => s.local);
  const rows = useAuthoring((s) => s.rows);
  const clear = useAuthoring((s) => s.clear);
  const encounterId = useEncounter((s) => s.encounterId);
  const controlled = useEncounter((s) => s.controlled);
  const locks = useLocks();
  // while the last turn plays out there is nothing to undo or lock: the next turn's plan opens after it
  const playing = usePlayback((s) => s.playing);
  const undoSlot = undoSlotOf(heroId, slot, local, rows);
  const mine = locks.mine;
  // every hero of mine has something in an action slot: the plan looks done, so the lock glows
  const planned = controlled.length > 0 && controlled.every((h) => SLOTS.some((s) => local.has(`${h}:${s}`) || rows.has(`${h}:${s}`)));
  const waiting = locks.waiting.filter((w) => w !== 'you');
  // "1/3 locked", and who the turn waits on once two or fewer are left
  const status = locks.total <= 1 ? undefined
    : `${locks.locked}/${locks.total} locked${locks.waiting.length > 0 && locks.waiting.length <= 2 ? ` · waiting on ${locks.waiting.join(' & ')}` : ''}`;
  return (
    <div className="fk-hud__plan-buttons">
      <button type="button" className="fk-hud__plate fk-hit" disabled={playing || heroId === undefined || mine || undoSlot === undefined} onClick={() => { if (heroId !== undefined && undoSlot !== undefined) clear(heroId, undoSlot); }}
        title={mine ? 'Unlock the plan to change it' : undoSlot !== undefined ? `Clear slot ${undoSlot}` : 'Nothing planned to undo'} data-testid="undo-button">Undo</button>
      <button type="button" className={`fk-hud__plate fk-hud__plate--lock fk-hit ${mine ? 'fk-hud__plate--locked' : planned ? 'fk-hud__plate--due' : ''}`} disabled={playing || !up || !controlled.length || !encounterId} aria-pressed={mine} aria-keyshortcuts={ariaKeys('board.ready', keys)}
        title={!up ? OFFLINE_TITLE : playing ? 'The turn is playing out. Plan the next one when it ends.' : mine ? (waiting.length ? `Your plan is locked. Waiting on ${waiting.join(', ')}.` : 'Your plan is locked.') : 'Lock every hero\'s plan (empty slots hold). The turn plays once everyone has locked.'}
        onClick={toggleReady} data-sfx="none" data-testid="ready-button" data-locked={locks.locked} data-total={locks.total}>
        <span className="fk-hud__plate-word"><i className={`fk-hud__padlock ${mine ? 'fk-hud__padlock--shut' : ''}`} aria-hidden="true" />{mine ? 'Unlock plan' : 'Lock plan'}</span>
        <small data-testid="lock-status">{status ? <span className="fk-hud__lock-status">{status}</span> : <><HotkeyKbd id="board.ready" /> {mine ? 'to change it' : 'when ready'}</>}</small>
      </button>
    </div>
  );
}

/**
 * Who has locked this turn: every human with a hero here (the seats), mine first as "you". Once the
 * pulse has locked, the server's unready list stands (an absent player's heroes held for them).
 */
export function useLocks(): { total: number; locked: number; mine: boolean; waiting: string[] } {
  const encounterId = useEncounter((s) => s.encounterId);
  const worldId = useEncounter((s) => s.worldId);
  const owners = useEncounter((s) => s.owners);
  const me = useEncounter((s) => s.me);
  const ready = useReadyRows(encounterId);
  const pulse = usePulse(encounterId);
  const names = useNames(worldId);
  const { seats } = useSeats();
  const readySet = new Set(ready.filter((r) => r.ready).map((r) => identityHex(r.owner)));
  const humans = [...seats.keys()];
  for (const h of new Set(Object.values(owners))) if (!humans.includes(h)) humans.push(h);
  const unready = pulse?.locked ? new Set(pulse.unready.map(identityHex)) : new Set(humans.filter((h) => !readySet.has(h)));
  const label = (id: string) => (id === me ? 'you' : names[id] ?? id.slice(-6));
  const waiting = humans.filter((h) => unready.has(h)).sort((a, b) => Number(b === me) - Number(a === me)).map(label);
  return { total: humans.length, locked: humans.length - waiting.length, mine: !!me && humans.includes(me) && !unready.has(me), waiting };
}
